// Undo — Phase 05a. main.js's saveState / withCheckpointHeld / triggerUndo, the
// in-widget undo net and the Undo button + Cmd/Ctrl+Z, moved here. The history
// itself lives in the store (pushCheckpoint / popCheckpoint / dropLastCheckpoint).
//
// Ben's #179 rules, kept exactly:
//   - one step per action, taken BEFORE the change (a step taken after would
//     already include the change, so undoing it would do nothing);
//   - while a drag's drop runs (merge / switch / overlay), checkpoints are HELD —
//     the drag's own start checkpoint covers it, so one Undo undoes the lot;
//   - a checkpoint that turned out to be a no-op (a header click, a cancelled
//     side-panel drag) is taken back off;
//   - changes INSIDE a widget (season, chips, toggles…) are caught by one net
//     rather than wiring a checkpoint into every control;
//   - 50 steps (UNDO_LIMIT).
//
// A step is read off the `.placed-module` elements: until 05e / 05f make the store's
// geometry live, the element is where it's current, and the `_`-state lives there for
// good. The player is read off the `.player-select` although the store's is live (05c):
// every writer sets the select first, and the pick handler's "put the previous player
// back for the checkpoint" relies on that. A restore rebuilds through the store (05b), one
// render per module (05i-1).

import { useProtoStore } from "../store/store.js";
import type { Snapshot, SnapshotModule } from "../store/types.js";
import { captureCustomState } from "../store/capture-custom-state.js";
import { canvasApi } from "./shell-actions.js";
import { playerIdFromSelect } from "./module-shell.js";
import { dismissWelcomeScreen } from "./welcome.js";
import { liveBusy, whenLiveSettled } from "../live/query.js";

type ProtoStore = typeof useProtoStore;

/** What dropping one module onto another does (main.js resolveModuleDropAction). */
export type ModuleDropAction = "merge" | "switch" | "similarity-compare" | "show-match" | "show-team" | "attach-panel" | "team-merge";

/** The undo actions (the gestures, the net and the controls below call them). */
export interface UndoApi {
  /** Take one checkpoint now (unless held). Call BEFORE the change. */
  saveState: () => void;
  /** Run `fn` with checkpoints held — a drop's merge / switch adds no second step. */
  withCheckpointHeld: (fn: () => void) => void;
  /** Hold / release by hand — for main.js's side-panel drop, whose held block is a
   *  ~100-line try/finally (left as-is until 05h ports it). */
  setCheckpointsHeld: (held: boolean) => void;
  /** Take back a checkpoint that turned out to be a no-op. */
  dropLastCheckpoint: () => void;
  triggerUndo: () => void;
  /** Read the current canvas as one step (not pushed). */
  capture: () => Snapshot;
}

/** Build the undo actions over a store + a capture/restore pair. Split from the DOM
 *  wiring so the checkpoint rules are unit-testable without a browser. */
export function createUndo(store: ProtoStore, capture: () => Snapshot, restore: (step: Snapshot) => void): UndoApi {
  return {
    saveState: () => {
      if (store.getState().checkpointsHeld) return;
      store.getState().pushCheckpoint(capture());
    },
    withCheckpointHeld: (fn) => {
      store.getState().setCheckpointsHeld(true);
      try {
        fn();
      } finally {
        store.getState().setCheckpointsHeld(false);
      }
    },
    setCheckpointsHeld: (held) => store.getState().setCheckpointsHeld(held),
    dropLastCheckpoint: () => store.getState().dropLastCheckpoint(),
    triggerUndo: () => {
      const previous = store.getState().popCheckpoint();
      if (previous) restore(previous);
    },
    capture,
  };
}

function readInt(value: string | null, what: string, mod: Element): number {
  const n = Number.parseInt(value ?? "", 10);
  if (Number.isNaN(n)) throw new Error(`Undo capture: ${what} missing on a .placed-module (${mod.className})`);
  return n;
}

/** main.js captureState(): every placed module's geometry, pin, player and generic
 *  `_`-state, plus the workspace shift (the store's since 05e-1). */
export function captureFromDom(): Snapshot {
  const { workspaceShiftX, workspaceShiftY } = useProtoStore.getState();
  const modules: SnapshotModule[] = [];
  document.querySelectorAll<HTMLElement>(".placed-module").forEach((mod) => {
    const titleEl = mod.querySelector<HTMLElement>(".module-title");
    if (!titleEl) throw new Error("Undo capture: a .placed-module has no .module-title");
    const playerSelect = mod.querySelector<HTMLSelectElement>(".player-select");
    modules.push({
      title: titleEl.innerText,
      // `|| "0"` as main.js's parseInt(style.left || 0): a module not yet positioned sits at 0.
      x: Number.parseInt(mod.style.left || "0", 10),
      y: Number.parseInt(mod.style.top || "0", 10),
      cols: readInt(mod.getAttribute("data-base-cols"), "data-base-cols", mod),
      rows: readInt(mod.getAttribute("data-base-rows"), "data-base-rows", mod),
      pinned: mod.getAttribute("data-pinned") === "true",
      player: playerSelect ? playerIdFromSelect(playerSelect.value) : null,
      custom: captureCustomState(mod as unknown as Record<string, unknown>),
    });
  });
  return { wsShiftX: workspaceShiftX, wsShiftY: workspaceShiftY, modules };
}

/** main.js triggerUndo()'s rebuild: clear the canvas, then recreate each module with its
 *  `_`-state. Since 05i-1 the state is on the element before the first render, so each
 *  module renders once (main.js rendered, then assigned the state and rendered again). */
export function restoreToDom(step: Snapshot): void {
  const canvas = canvasApi();
  useProtoStore.getState().setWorkspaceShift(step.wsShiftX, step.wsShiftY);
  canvas.clearModules();
  dismissWelcomeScreen();
  step.modules.forEach((item) => {
    canvas.createModule({
      x: item.x,
      y: item.y,
      cols: item.cols,
      rows: item.rows,
      title: item.title,
      pinned: item.pinned,
      player: item.player,
      custom: item.custom,
    });
  });
}

/** One noted interaction for the undo net: the state when it began. */
export interface PendingInteraction {
  state: Snapshot;
  json: string;
  changes: number;
}

/**
 * The undo net's decision, once an interaction has settled: push the state it began
 * with — but only if nothing else took (or undid) a checkpoint meanwhile, and some
 * widget's saved state actually changed. `currentJson` is lazy so the canvas is only
 * read again when the first test passes (as main.js).
 */
export function netStepToPush(p: PendingInteraction, historyChangesNow: number, currentJson: () => string): Snapshot | null {
  if (historyChangesNow !== p.changes) return null; // someone else checkpointed / undid
  if (currentJson() === p.json) return null; // nothing changed (e.g. opening a dropdown)
  return p.state;
}

/**
 * The in-widget undo net (Ben's #179). Layout actions take their own checkpoints, but
 * the controls INSIDE a widget never did, so Undo skipped them. One net for all of
 * them: note the state when an interaction starts; once it has settled, if a widget
 * changed and nothing else checkpointed, the noted state becomes the undo step.
 *
 * Capture phase, so the state is noted before any widget's own handler runs. Pointer:
 * note on press, check on release (covers slider drags). Keyboard: only Enter / Space
 * (typing in a search box changes nothing until a pick). Faithful quirk, kept: Enter /
 * Space while typing in Notes also counts, so each word can become a step (logged for Ben).
 */
export function installUndoNet(store: ProtoStore, undo: UndoApi): void {
  let pending: PendingInteraction | null = null;
  let settling = false;

  function begin(): void {
    if (pending) return; // keep the earliest state of an ongoing interaction
    const state = undo.capture();
    pending = { state, json: JSON.stringify(state.modules), changes: store.getState().historyChanges };
  }

  // Two frames: React commits (and mirrors its state onto the element), then any
  // follow-up render has run. With ?live=1 a widget's change can wait on its data
  // (Track 2): while a live call is on its way, wait for it to land, then two more frames
  // (it may start the next call — a league's table after its season list). On the
  // snapshot site nothing is ever on its way, so this is the same two frames as before.
  function settle(): void {
    if (!pending || settling) return;
    settling = true;
    const decide = () => {
      if (liveBusy()) {
        void whenLiveSettled().then(() => requestAnimationFrame(() => requestAnimationFrame(decide)));
        return;
      }
      settling = false;
      const p = pending;
      pending = null;
      if (!p) return;
      const step = netStepToPush(p, store.getState().historyChanges, () => JSON.stringify(undo.capture().modules));
      // Straight onto the history — not saveState(), which a held checkpoint would skip.
      if (step) store.getState().pushCheckpoint(step);
    };
    requestAnimationFrame(() => requestAnimationFrame(decide));
  }

  document.addEventListener("pointerdown", begin, true);
  document.addEventListener("pointerup", settle, true);
  document.addEventListener("pointercancel", settle, true);
  document.addEventListener(
    "keydown",
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        begin();
        settle();
      }
    },
    true,
  );
  document.addEventListener(
    "change",
    () => {
      begin();
      settle();
    },
    true,
  );
}

/** The header Undo button + Cmd/Ctrl+Z. */
export function installUndoControls(undo: UndoApi): void {
  const btn = document.getElementById("undo-btn");
  if (!btn) throw new Error("#undo-btn missing from legacy-scaffold.html");
  btn.addEventListener("click", () => undo.triggerUndo());

  document.addEventListener("keydown", (e) => {
    // Focus inside a text field / textarea / contenteditable keeps the browser's own
    // text undo — otherwise fixing a typo in a note would revert the whole canvas.
    const active = document.activeElement as HTMLElement | null;
    const isEditableFocus =
      !!active && (active.tagName === "TEXTAREA" || active.tagName === "INPUT" || active.isContentEditable);
    if (!isEditableFocus && (e.ctrlKey || e.metaKey) && e.key === "z") {
      e.preventDefault();
      undo.triggerUndo();
    }
  });
}

/**
 * Wire undo into the live page. Called where main.js loaded (gone since 05k-1), so the
 * net's and the shortcut's document listeners register at the same point main.js
 * registered them (after the renderers', before tour.js's).
 */
export function installUndo(store: ProtoStore): UndoApi {
  const undo = createUndo(store, captureFromDom, restoreToDom);
  installUndoNet(store, undo);
  installUndoControls(undo);
  installed = undo;
  return undo;
}

let installed: UndoApi | null = null;

/** The installed undo actions, for ported gestures (the move takes its own checkpoint). */
export function undoApi(): UndoApi {
  if (!installed) throw new Error("installUndo() hasn't run");
  return installed;
}
